/**
 * ============================================================================
 * USER PAGE: Tiny URL & Dynamic Redirection Management Platform
 * ============================================================================
 * Purpose:
 *   Full-featured URL shortening, dynamic path forwarding, expiration controls,
 *   telemetry analytics, and Bulk Phone Campaigns (Anti-Tamper) matching Screenshot 1.
 * ============================================================================
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  LinkIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  ChartBarIcon,
  PencilSquareIcon,
  TrashIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentCheckIcon,
  ArrowTopRightOnSquareIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  SparklesIcon,
  PhoneIcon,
  UserGroupIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";

import tinyUrlService from "../../services/tinyUrlService";
import { getCurrentUser, hasPermission } from "../../../core/utils/auth";
import BulkCreateTinyUrlDrawer from "./BulkCreateTinyUrlDrawer";
import ViewPhoneNumbersModal from "./ViewPhoneNumbersModal";

export default function TinyUrls() {
  const navigate = useNavigate();
  const currentUser = getCurrentUser();

  // Permission Check
  const isPermitted = hasPermission(currentUser, "TINY_URL_MANAGEMENT");

  // Tab State: 'campaigns' (Bulk Phone Campaigns) | 'single' (Single Tiny URLs)
  const [activeTab, setActiveTab] = useState("campaigns");

  // Frontend origin for building short links
  const frontendBase = window.location.origin;

  // ─── Bulk Phone Campaigns State (Screenshot 1) ───
  const [campaigns, setCampaigns] = useState([]);
  const [campaignStats, setCampaignStats] = useState({
    activeCampaigns: 0,
    currentPageContacts: 0,
    currentPageClicks: 0,
    totalCampaigns: 0,
  });
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [campaignPagination, setCampaignPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const [campaignSearch, setCampaignSearch] = useState("");
  const [campaignStatusFilter, setCampaignStatusFilter] = useState("all");
  const [copiedCampaignCode, setCopiedCampaignCode] = useState(null);

  // View Phone Numbers Modal State (Screenshot 2)
  const [isViewPhoneModalOpen, setIsViewPhoneModalOpen] = useState(false);
  const [selectedCampaignForView, setSelectedCampaignForView] = useState(null);

  // ─── Single Tiny URLs State ───
  const [links, setLinks] = useState([]);
  const [stats, setStats] = useState({
    totalLinks: 0,
    activeLinks: 0,
    expiredLinks: 0,
    totalClicks: 0,
  });
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [copiedCode, setCopiedCode] = useState(null);

  // ─── Drawers State ───
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isBulkDrawerOpen, setIsBulkDrawerOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    originalDestinationUrl: "",
    description: "",
    expirationPreset: "never",
    customDate: "",
    customCode: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // ─── Fetch Bulk Phone Campaigns ───
  const fetchCampaigns = useCallback(async () => {
    if (!isPermitted) return;
    setCampaignLoading(true);
    try {
      const res = await tinyUrlService.getCampaigns({
        page: campaignPagination.page,
        limit: campaignPagination.limit,
        search: campaignSearch.trim(),
        status: campaignStatusFilter,
      });

      // Handle both unwrapped and wrapped responses
      const campaignList =
        (Array.isArray(res?.data) ? res.data : null) ||
        (Array.isArray(res?.data?.data) ? res.data.data : null) ||
        (Array.isArray(res) ? res : []);

      const stats = res?.stats || res?.data?.stats || null;
      const pagination = res?.pagination || res?.data?.pagination || null;

      if (res && (res.success || Array.isArray(campaignList))) {
        setCampaigns(campaignList);
        if (stats) {
          setCampaignStats(stats);
        } else {
          const currentPageContacts = campaignList.reduce((sum, c) => sum + (c.totalContacts || 0), 0);
          const currentPageClicks = campaignList.reduce((sum, c) => sum + (c.totalClicks || 0), 0);
          setCampaignStats({
            activeCampaigns: campaignList.filter((c) => c.isActive !== false).length,
            currentPageContacts,
            currentPageClicks,
            totalCampaigns: pagination?.total ?? campaignList.length,
          });
        }
        if (pagination) {
          setCampaignPagination((prev) => ({ ...prev, ...pagination }));
        }
      }
    } catch (err) {
      console.error("Failed to load bulk phone campaigns:", err);
    } finally {
      setCampaignLoading(false);
    }
  }, [
    isPermitted,
    campaignPagination.page,
    campaignPagination.limit,
    campaignSearch,
    campaignStatusFilter,
  ]);

  // ─── Fetch Single Tiny URLs ───
  const fetchLinks = useCallback(async () => {
    if (!isPermitted) return;
    setLoading(true);
    try {
      const res = await tinyUrlService.list({
        page: pagination.page,
        limit: pagination.limit,
        search: search.trim(),
        status: statusFilter,
        sortBy,
      });

      if (res && res.success) {
        setLinks(res.data || []);
        if (res.pagination) {
          setPagination((prev) => ({ ...prev, ...res.pagination }));
        }
        if (res.stats) {
          setStats(res.stats);
        }
      }
    } catch (err) {
      console.error("Failed to load tiny URLs:", err);
    } finally {
      setLoading(false);
    }
  }, [isPermitted, pagination.page, pagination.limit, search, statusFilter, sortBy]);

  // Trigger fetches on mount or dependency changes
  useEffect(() => {
    if (activeTab === "campaigns") {
      fetchCampaigns();
    } else {
      fetchLinks();
    }
  }, [activeTab, fetchCampaigns, fetchLinks]);

  // Copy helper
  const handleCopy = (text, code, isCampaign = false) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    if (isCampaign) {
      setCopiedCampaignCode(code);
      setTimeout(() => setCopiedCampaignCode(null), 2000);
    } else {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    }
  };

  // Open Drawer for Create Single Link
  const handleOpenCreate = () => {
    setEditingLink(null);
    setFormData({
      title: "",
      originalDestinationUrl: "",
      description: "",
      expirationPreset: "never",
      customDate: "",
      customCode: "",
    });
    setIsDrawerOpen(true);
  };

  // Open Drawer for Edit Single Link
  const handleOpenEdit = (link) => {
    setEditingLink(link);
    let preset = "never";
    let dateStr = "";
    if (link.expirationDate) {
      preset = "custom";
      try {
        dateStr = new Date(link.expirationDate).toISOString().slice(0, 16);
      } catch (e) {
        dateStr = "";
      }
    }
    setFormData({
      title: link.title || "",
      originalDestinationUrl: link.originalDestinationUrl || "",
      description: link.description || "",
      expirationPreset: preset,
      customDate: dateStr,
      customCode: link.code || "",
    });
    setIsDrawerOpen(true);
  };

  // Close Drawer
  const handleCloseDrawer = () => {
    if (submitting) return;
    setIsDrawerOpen(false);
    setEditingLink(null);
  };

  // Submit Single Link Drawer Form
  const handleSubmitDrawer = async (e) => {
    e.preventDefault();

    if (!formData.originalDestinationUrl.trim()) {
      Swal.fire({
        title: "Destination URL Required",
        text: "Please provide a valid destination URL to redirect visitors to.",
        icon: "warning",
        confirmButtonColor: "#3a57e8",
      });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        title: formData.title.trim() || "Untitled Link",
        description: formData.description.trim(),
        originalDestinationUrl: formData.originalDestinationUrl.trim(),
        expirationPreset: formData.expirationPreset,
        expirationDate: formData.expirationPreset === "custom" ? formData.customDate : null,
      };

      if (!editingLink && formData.customCode.trim()) {
        payload.customCode = formData.customCode.trim().toUpperCase();
      }

      if (editingLink) {
        await tinyUrlService.update(editingLink._id, payload);
        Swal.fire({
          title: "Tiny URL Updated!",
          text: `Link "${formData.title || editingLink.code}" has been successfully updated.`,
          icon: "success",
          timer: 1600,
          showConfirmButton: false,
        });
      } else {
        const res = await tinyUrlService.create(payload);
        const code = res?.data?.code || "LINK";
        const shortUrl = `${frontendBase}/${code}`;
        Swal.fire({
          title: "Tiny URL Created!",
          html: `<div class="text-xs text-gray-600 dark:text-gray-300">Your new short link is ready:<br/><b class="text-[#3a57e8] font-mono text-sm inline-block mt-2">${shortUrl}</b></div>`,
          icon: "success",
          confirmButtonColor: "#3a57e8",
        });
      }

      setIsDrawerOpen(false);
      setEditingLink(null);
      fetchLinks();
    } catch (err) {
      Swal.fire({
        title: editingLink ? "Update Failed" : "Creation Failed",
        text: err.response?.data?.error || err.message || "An unexpected error occurred",
        icon: "error",
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle Single Link Active/Inactive Status
  const handleToggleStatus = async (link) => {
    try {
      const newStatus = !link.isActive;
      await tinyUrlService.toggleStatus(link._id, newStatus);
      setLinks((prev) =>
        prev.map((l) => (l._id === link._id ? { ...l, isActive: newStatus } : l))
      );
    } catch (err) {
      Swal.fire({
        title: "Status Toggle Failed",
        text: err.response?.data?.error || err.message,
        icon: "error",
      });
    }
  };

  // Delete Single Link
  const handleDeleteLink = async (link) => {
    const confirm = await Swal.fire({
      title: "Delete Tiny URL?",
      html: `Are you sure you want to delete short code <b class="text-red-500">${link.code}</b>?<br/><span class="text-xs text-gray-500">Visitors will receive a 404 Link Not Found page.</span>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
    });

    if (!confirm.isConfirmed) return;

    try {
      await tinyUrlService.delete(link._id);
      Swal.fire({
        title: "Deleted!",
        text: `Tiny URL "${link.code}" has been deleted.`,
        icon: "success",
        timer: 1400,
        showConfirmButton: false,
      });
      fetchLinks();
    } catch (err) {
      Swal.fire({
        title: "Deletion Failed",
        text: err.response?.data?.error || err.message,
        icon: "error",
      });
    }
  };

  // Delete Campaign
  const handleDeleteCampaign = async (campaign) => {
    const confirm = await Swal.fire({
      title: "Delete Bulk Campaign?",
      html: `Are you sure you want to delete campaign <b class="text-red-500">${campaign.title}</b> (${campaign.code})?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
    });

    if (!confirm.isConfirmed) return;

    try {
      await tinyUrlService.deleteCampaign(campaign._id);
      Swal.fire({
        title: "Deleted!",
        text: `Campaign "${campaign.title}" has been removed.`,
        icon: "success",
        timer: 1400,
        showConfirmButton: false,
      });
      fetchCampaigns();
    } catch (err) {
      Swal.fire({
        title: "Deletion Failed",
        text: err.response?.data?.error || err.message,
        icon: "error",
      });
    }
  };

  // Unauthorized Screen
  if (!isPermitted) {
    return (
      <div className="p-8 max-w-lg mx-auto text-center mt-12 bg-white dark:bg-gray-800 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-700">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 dark:bg-red-950/50 text-red-500 flex items-center justify-center">
          <ExclamationTriangleIcon className="w-8 h-8" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">403 - Forbidden</h2>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          You do not have the required permission (<code>TINY_URL_MANAGEMENT</code>) to access the Tiny URL Management Platform. Please contact your account administrator.
        </p>
        <button
          onClick={() => navigate("/")}
          className="mt-6 px-5 py-2.5 rounded-xl bg-[#3a57e8] hover:bg-[#2b44cb] text-white text-xs font-semibold cursor-pointer shadow-md"
        >
          Return to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 mx-auto">
      {/* ─── Mode Tab Selector ─── */}
      <div className="flex items-center justify-between border-b border-gray-200/80 dark:border-gray-700/80 pb-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveTab("campaigns")}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold cursor-pointer transition-all ${
              activeTab === "campaigns"
                ? "bg-[#3a57e8] text-white shadow-md shadow-[#3a57e8]/25"
                : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-750 border border-gray-200/80 dark:border-gray-700"
            }`}
          >
            <SparklesIcon className="w-4 h-4" />
            Bulk Phone Campaigns
            
          </button>
          <button
            onClick={() => setActiveTab("single")}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold cursor-pointer transition-all ${
              activeTab === "single"
                ? "bg-[#3a57e8] text-white shadow-md shadow-[#3a57e8]/25"
                : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-750 border border-gray-200/80 dark:border-gray-700"
            }`}
          >
            <LinkIcon className="w-4 h-4" />
            Single Tiny URLs
          </button>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* ─── BULK PHONE CAMPAIGNS VIEW (MATCHING SCREENSHOT 1) ─────────────── */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {activeTab === "campaigns" && (
        <>
          {/* Header Row (Screenshot 1) */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2.5 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                  Bulk Phone Campaigns
                </h1>
               
              </div>
              
            </div>

            <div className="flex items-center gap-3">
              {/* Circular Refresh button */}
              <button
                onClick={fetchCampaigns}
                disabled={campaignLoading}
                className="w-9 h-9 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-center shadow-xs cursor-pointer transition-colors"
                title="Refresh campaigns"
              >
                <ArrowPathIcon
                  className={`w-4 h-4 ${campaignLoading ? "animate-spin text-[#3a57e8]" : ""}`}
                />
              </button>

              {/* + Upload & Create Bulk Campaign Button (Purple) */}
              <button
                onClick={() => setIsBulkDrawerOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#4f46e5] hover:bg-[#4338ca] text-white text-xs font-bold shadow-md shadow-[#4f46e5]/30 cursor-pointer transition-all duration-150"
              >
                <PlusIcon className="w-4 h-4 stroke-[2.5]" />
                Upload & Create Bulk Campaign
              </button>
            </div>
          </div>

          {/* 3 Stat Cards (Screenshot 1) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Card 1: ACTIVE CAMPAIGNS */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200/80 dark:border-gray-700/80 shadow-xs flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0">
                <Squares2X2Icon className="w-6 h-6 stroke-[1.8]" />
              </div>
              <div>
                <span className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider block">
                  ACTIVE CAMPAIGNS
                </span>
                <span className="text-2xl font-bold text-gray-900 dark:text-white mt-1 block">
                  {campaignStats.activeCampaigns || campaigns.length}
                </span>
              </div>
            </div>

            {/* Card 2: CONTACTS (CURRENT PAGE) */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200/80 dark:border-gray-700/80 shadow-xs flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                <UserGroupIcon className="w-6 h-6 stroke-[1.8]" />
              </div>
              <div>
                <span className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider block">
                  CONTACTS (CURRENT PAGE)
                </span>
                <span className="text-2xl font-bold text-gray-900 dark:text-white mt-1 block">
                  {campaignStats.currentPageContacts || 0}
                </span>
              </div>
            </div>

            {/* Card 3: TOTAL CLICKS (CURRENT PAGE) */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200/80 dark:border-gray-700/80 shadow-xs flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-amber-50 dark:bg-amber-950/60 text-amber-500 dark:text-amber-400 flex items-center justify-center shrink-0">
                <SparklesIcon className="w-6 h-6 stroke-[1.8]" />
              </div>
              <div>
                <span className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider block">
                  TOTAL CLICKS (CURRENT PAGE)
                </span>
                <span className="text-2xl font-bold text-gray-900 dark:text-white mt-1 block">
                  {campaignStats.currentPageClicks || 0}
                </span>
              </div>
            </div>
          </div>

          {/* Search & Filter Bar (Screenshot 1) */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-200/80 dark:border-gray-700/80 shadow-xs flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            {/* Search Input */}
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by code, campaign title, or URL..."
                value={campaignSearch}
                onChange={(e) => {
                  setCampaignSearch(e.target.value);
                  setCampaignPagination((p) => ({ ...p, page: 1 }));
                }}
                className="w-full pl-10 pr-4 py-2 text-xs rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-700/50 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4f46e5] transition-all"
              />
            </div>

            {/* Status Dropdown */}
            <div className="shrink-0">
              <select
                value={campaignStatusFilter}
                onChange={(e) => {
                  setCampaignStatusFilter(e.target.value);
                  setCampaignPagination((p) => ({ ...p, page: 1 }));
                }}
                className="px-3 py-2 text-xs rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#4f46e5]"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>

          {/* Table (Screenshot 1) */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/80 dark:border-gray-700/80 overflow-hidden shadow-xs">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-700/80 bg-gray-50/60 dark:bg-gray-750/50 text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <th className="py-3 px-4">CODE</th>
                    <th className="py-3 px-4">TITLE / DESCRIPTION</th>
                    <th className="py-3 px-4">TINY BASE URL</th>
                    <th className="py-3 px-4">ORIGINAL DESTINATION URL</th>
                    <th className="py-3 px-4 text-center">CLICKS</th>
                    <th className="py-3 px-4 text-center">STATUS</th>
                    <th className="py-3 px-4">EXPIRES</th>
                    <th className="py-3 px-4">CREATED</th>
                    <th className="py-3 px-4 text-right">ACTION</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-750">
                  {campaignLoading ? (
                    <tr>
                      <td colSpan={9} className="py-12 text-center text-gray-400">
                        <ArrowPathIcon className="w-6 h-6 mx-auto animate-spin text-[#4f46e5] mb-2" />
                        Loading campaigns...
                      </td>
                    </tr>
                  ) : campaigns.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="py-12 text-center text-gray-400">
                        <UserGroupIcon className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                        No bulk phone campaigns found. Click &ldquo;Upload & Create Bulk Campaign&rdquo; to generate your first campaign!
                      </td>
                    </tr>
                  ) : (
                    campaigns.map((c) => {
                      const isExpired =
                        c.expirationDate && new Date(c.expirationDate).getTime() <= Date.now();
                      const isCopied = copiedCampaignCode === c.code;
                      const baseUrl = c.tinyBaseUrl || frontendBase;

                      return (
                        <tr
                          key={c._id || c.code}
                          className="hover:bg-blue-50/30 dark:hover:bg-gray-750/40 transition-colors"
                        >
                          {/* CODE */}
                          <td className="py-3.5 px-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold text-xs text-[#4f46e5] bg-indigo-50 dark:bg-indigo-900/40 px-2.5 py-1 rounded-lg border border-indigo-200/60 dark:border-indigo-800/40">
                                {c.code}
                              </span>
                              <button
                                onClick={() => handleCopy(c.code, c.code, true)}
                                title="Copy campaign code"
                                className="p-1 rounded-md text-gray-400 hover:text-[#4f46e5] hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                              >
                                {isCopied ? (
                                  <ClipboardDocumentCheckIcon className="w-4 h-4 text-emerald-500" />
                                ) : (
                                  <ClipboardDocumentIcon className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                          </td>

                          {/* TITLE / DESCRIPTION + Contacts Badge */}
                          <td className="py-3.5 px-4 max-w-xs sm:max-w-sm">
                            <div className="font-semibold text-gray-900 dark:text-white truncate">
                              {c.title || "Untitled Campaign"}
                            </div>
                            <div className="mt-1 flex items-center gap-1.5">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                <UserGroupIcon className="w-3 h-3 text-gray-500" />
                                {c.totalContacts || 0} Contacts
                              </span>
                              {c.description && (
                                <span className="text-[11px] text-gray-400 truncate max-w-[180px]">
                                  • {c.description}
                                </span>
                              )}
                            </div>
                          </td>

                          {/* TINY BASE URL */}
                          <td className="py-3.5 px-4 text-gray-600 dark:text-gray-300 whitespace-nowrap text-xs font-mono">
                            {baseUrl}
                          </td>

                          {/* ORIGINAL DESTINATION URL */}
                          <td className="py-3.5 px-4 max-w-xs sm:max-w-md">
                            <div
                              className="text-xs text-gray-600 dark:text-gray-300 truncate max-w-sm font-mono"
                              title={c.originalDestinationUrl}
                            >
                              {c.originalDestinationUrl}
                            </div>
                          </td>

                          {/* CLICKS */}
                          <td className="py-3.5 px-4 text-center whitespace-nowrap">
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-50 dark:bg-blue-950/40 text-[#3a57e8] dark:text-blue-300 border border-blue-200/60 dark:border-blue-800/60">
                              <SparklesIcon className="w-3.5 h-3.5 text-[#3a57e8]" />
                              {c.totalClicks || 0}
                            </span>
                          </td>

                          {/* STATUS */}
                          <td className="py-3.5 px-4 text-center whitespace-nowrap">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${
                                isExpired
                                  ? "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300"
                                  : c.isActive
                                  ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400"
                                  : "bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400"
                              }`}
                            >
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${
                                  isExpired
                                    ? "bg-gray-400"
                                    : c.isActive
                                    ? "bg-emerald-500 animate-pulse"
                                    : "bg-amber-500"
                                }`}
                              />
                              {isExpired ? "Expired" : c.isActive ? "Active" : "Inactive"}
                            </span>
                          </td>

                          {/* EXPIRES */}
                          <td className="py-3.5 px-4 whitespace-nowrap text-gray-500">
                            {c.expirationDate ? (
                              new Date(c.expirationDate).toLocaleDateString()
                            ) : (
                              <span className="text-gray-400 italic">Never</span>
                            )}
                          </td>

                          {/* CREATED */}
                          <td className="py-3.5 px-4 whitespace-nowrap text-gray-500">
                            {c.createdAt
                              ? new Date(c.createdAt).toLocaleDateString("en-US", {
                                  month: "numeric",
                                  day: "numeric",
                                  year: "numeric",
                                })
                              : "-"}
                          </td>

                          {/* ACTION: 📞 View Phone Numbers (Matching Screenshot 1) */}
                          <td className="py-3.5 px-4 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                onClick={() => {
                                  setSelectedCampaignForView(c);
                                  setIsViewPhoneModalOpen(true);
                                }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-indigo-200 dark:border-indigo-800/60 bg-indigo-50/70 hover:bg-indigo-100/70 dark:bg-indigo-950/40 dark:hover:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 text-xs font-semibold shadow-xs cursor-pointer transition-all duration-150"
                                title="View individual phone numbers, tokens & click tracking"
                              >
                                <PhoneIcon className="w-3.5 h-3.5 stroke-[2]" />
                                View Phone Numbers
                              </button>

                              <button
                                onClick={() => handleDeleteCampaign(c)}
                                className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 cursor-pointer transition-colors"
                                title="Delete campaign"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Bottom Pagination */}
            <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700/80 flex flex-col sm:flex-row items-center justify-between gap-3 bg-white dark:bg-gray-800">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Showing{" "}
                <span className="font-semibold text-gray-700 dark:text-gray-200">
                  {campaigns.length === 0 ? 0 : (campaignPagination.page - 1) * campaignPagination.limit + 1}
                </span>{" "}
                to{" "}
                <span className="font-semibold text-gray-700 dark:text-gray-200">
                  {Math.min(campaignPagination.page * campaignPagination.limit, campaignPagination.total || campaigns.length)}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-gray-700 dark:text-gray-200">
                  {campaignPagination.total || campaigns.length}
                </span>{" "}
                campaigns
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    setCampaignPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))
                  }
                  disabled={campaignPagination.page <= 1}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                >
                  Previous
                </button>
                <span className="text-xs text-gray-600 dark:text-gray-400 font-semibold px-2">
                  Page {campaignPagination.page} of {campaignPagination.totalPages || 1}
                </span>
                <button
                  onClick={() =>
                    setCampaignPagination((p) => ({
                      ...p,
                      page: Math.min(p.totalPages || 1, p.page + 1),
                    }))
                  }
                  disabled={campaignPagination.page >= (campaignPagination.totalPages || 1)}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* ─── SINGLE TINY URLS VIEW ────────────────────────────────────────── */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {activeTab === "single" && (
        <>
          {/* Top Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-xl bg-[#3a57e8]/10 text-[#3a57e8] flex items-center justify-center shadow-xs">
                  <LinkIcon className="w-5 h-5" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                    Single Tiny URLs & Redirection
                  </h1>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Shorten, forward, and track smart dynamic URLs with subpaths and telemetry
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={fetchLinks}
                disabled={loading}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-xs font-semibold shadow-xs cursor-pointer transition-colors"
                title="Refresh list"
              >
                <ArrowPathIcon className={`w-4 h-4 ${loading ? "animate-spin text-[#3a57e8]" : ""}`} />
                Refresh
              </button>
              <button
                onClick={handleOpenCreate}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#3a57e8] hover:bg-[#2e47d4] text-white text-xs font-semibold shadow-md shadow-[#3a57e8]/30 cursor-pointer transition-all duration-150"
              >
                <PlusIcon className="w-4 h-4 stroke-[2.5]" />
                Create Tiny URL
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-5 border border-gray-200/80 dark:border-gray-700/80 shadow-xs">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Total Links
                </span>
                <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-[#3a57e8] flex items-center justify-center">
                  <LinkIcon className="w-4 h-4" />
                </div>
              </div>
              <div className="mt-3 text-2xl font-bold text-gray-900 dark:text-white">
                {stats.totalLinks.toLocaleString()}
              </div>
              <span className="text-[11px] text-gray-400">Scoped to your tenant account</span>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-5 border border-gray-200/80 dark:border-gray-700/80 shadow-xs">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Active Links
                </span>
                <div className="w-9 h-9 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 flex items-center justify-center">
                  <CheckCircleIcon className="w-4 h-4" />
                </div>
              </div>
              <div className="mt-3 text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                {stats.activeLinks.toLocaleString()}
              </div>
              <span className="text-[11px] text-gray-400">Currently redirecting</span>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-5 border border-gray-200/80 dark:border-gray-700/80 shadow-xs">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Total Clicks
                </span>
                <div className="w-9 h-9 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 flex items-center justify-center">
                  <ChartBarIcon className="w-4 h-4" />
                </div>
              </div>
              <div className="mt-3 text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                {stats.totalClicks.toLocaleString()}
              </div>
              <span className="text-[11px] text-gray-400">Telemetry tracked visits</span>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-5 border border-gray-200/80 dark:border-gray-700/80 shadow-xs">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Expired Links
                </span>
                <div className="w-9 h-9 rounded-xl bg-amber-50 dark:bg-amber-900/30 text-amber-600 flex items-center justify-center">
                  <ClockIcon className="w-4 h-4" />
                </div>
              </div>
              <div className="mt-3 text-2xl font-bold text-amber-600 dark:text-amber-400">
                {stats.expiredLinks.toLocaleString()}
              </div>
              <span className="text-[11px] text-gray-400">Past expiration threshold</span>
            </div>
          </div>

          {/* Search, Status & Sorting Bar */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-200/80 dark:border-gray-700/80 shadow-xs flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by title, code, or destination URL..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
                className="w-full pl-10 pr-4 py-2 text-xs rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-700/50 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3a57e8] transition-all"
              />
            </div>

            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 p-1 rounded-xl bg-gray-100 dark:bg-gray-700">
                {["all", "active", "inactive", "expired"].map((st) => (
                  <button
                    key={st}
                    onClick={() => {
                      setStatusFilter(st);
                      setPagination((p) => ({ ...p, page: 1 }));
                    }}
                    className={`px-3 py-1 text-xs font-semibold rounded-lg capitalize cursor-pointer transition-colors ${
                      statusFilter === st
                        ? "bg-white dark:bg-gray-800 text-[#3a57e8] shadow-xs"
                        : "text-gray-600 dark:text-gray-300 hover:text-gray-900"
                    }`}
                  >
                    {st}
                  </button>
                ))}
              </div>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 text-xs rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#3a57e8]"
              >
                <option value="newest">Newest First</option>
                <option value="most_clicks">Most Clicks</option>
                <option value="oldest">Oldest First</option>
                <option value="title">Alphabetical (A-Z)</option>
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/80 dark:border-gray-700/80 overflow-hidden shadow-xs">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-700/80 bg-gray-50/60 dark:bg-gray-750/50 text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <th className="py-3 px-4">CODE</th>
                    <th className="py-3 px-4">TITLE / DESTINATION</th>
                    <th className="py-3 px-4">CREATED</th>
                    <th className="py-3 px-4">EXPIRES</th>
                    <th className="py-3 px-4 text-center">STATUS</th>
                    <th className="py-3 px-4 text-center">CLICKS</th>
                    <th className="py-3 px-4 text-right">ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-750">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-gray-400">
                        <ArrowPathIcon className="w-6 h-6 mx-auto animate-spin text-[#3a57e8] mb-2" />
                        Loading Tiny URLs...
                      </td>
                    </tr>
                  ) : links.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-gray-400">
                        <LinkIcon className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                        No Tiny URLs found. Click &ldquo;Create Tiny URL&rdquo; to shorten your first link!
                      </td>
                    </tr>
                  ) : (
                    links.map((link) => {
                      const isExpired =
                        link.expirationDate && new Date(link.expirationDate).getTime() <= Date.now();
                      const shortUrl = `${frontendBase}/${link.code}`;
                      const isCopied = copiedCode === link.code;

                      return (
                        <tr
                          key={link._id}
                          className="hover:bg-blue-50/30 dark:hover:bg-gray-750/40 transition-colors"
                        >
                          {/* Short Code & Copy */}
                          <td className="py-3.5 px-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold text-sm text-[#3a57e8] bg-blue-50 dark:bg-blue-900/30 px-2.5 py-1 rounded-lg border border-blue-200/50 dark:border-blue-800/40">
                                {link.code}
                              </span>
                              <button
                                onClick={() => handleCopy(shortUrl, link.code)}
                                title={`Copy Frontend Short URL: ${shortUrl}`}
                                className="p-1 rounded-md text-gray-400 hover:text-[#3a57e8] hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                              >
                                {isCopied ? (
                                  <ClipboardDocumentCheckIcon className="w-4 h-4 text-emerald-500" />
                                ) : (
                                  <ClipboardDocumentIcon className="w-4 h-4" />
                                )}
                              </button>
                              <a
                                href={shortUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={`Open Link Directly: ${shortUrl}`}
                                className="p-1 rounded-md text-gray-400 hover:text-[#3a57e8] hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                              >
                                <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                              </a>
                            </div>
                          </td>

                          {/* Title & Destination URL */}
                          <td className="py-3.5 px-4 max-w-xs sm:max-w-md">
                            <div className="font-semibold text-gray-900 dark:text-white truncate">
                              {link.title || "Untitled Link"}
                            </div>
                            <div
                              className="text-[11px] text-gray-400 truncate max-w-sm"
                              title={link.originalDestinationUrl}
                            >
                              {link.originalDestinationUrl}
                            </div>
                          </td>

                          {/* Created Date */}
                          <td className="py-3.5 px-4 text-gray-500 whitespace-nowrap">
                            {link.createdAt
                              ? new Date(link.createdAt).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })
                              : "-"}
                          </td>

                          {/* Expiration */}
                          <td className="py-3.5 px-4 whitespace-nowrap">
                            {isExpired ? (
                              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 bg-red-50 dark:bg-red-950/40 px-2 py-0.5 rounded-md border border-red-200 dark:border-red-800">
                                <ClockIcon className="w-3.5 h-3.5" />
                                Expired
                              </span>
                            ) : link.expirationDate ? (
                              <span className="text-[11px] text-gray-600 dark:text-gray-300">
                                {new Date(link.expirationDate).toLocaleString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                            ) : (
                              <span className="text-[11px] text-gray-400 italic">Never</span>
                            )}
                          </td>

                          {/* Status */}
                          <td className="py-3.5 px-4 text-center whitespace-nowrap">
                            <button
                              onClick={() => handleToggleStatus(link)}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold cursor-pointer transition-all ${
                                isExpired
                                  ? "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300"
                                  : link.isActive
                                  ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400 hover:bg-emerald-100"
                                  : "bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400 hover:bg-amber-100"
                              }`}
                            >
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${
                                  isExpired
                                    ? "bg-gray-400"
                                    : link.isActive
                                    ? "bg-emerald-500 animate-pulse"
                                    : "bg-amber-500"
                                }`}
                              />
                              {isExpired ? "Expired" : link.isActive ? "Active" : "Inactive"}
                            </button>
                          </td>

                          {/* Clicks */}
                          <td className="py-3.5 px-4 text-center whitespace-nowrap">
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-gray-700 font-bold text-gray-800 dark:text-gray-100">
                              {link.clickCount || 0}
                            </span>
                          </td>

                          {/* Actions */}
                          <td className="py-3.5 px-4 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => navigate(`/workspace/tiny-urls/${link._id}/analytics`)}
                                className="p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 cursor-pointer transition-colors"
                                title="View Analytics & Logs"
                              >
                                <ChartBarIcon className="w-4 h-4" />
                              </button>

                              <button
                                onClick={() => handleOpenEdit(link)}
                                className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 cursor-pointer transition-colors"
                                title="Edit Link"
                              >
                                <PencilSquareIcon className="w-4 h-4" />
                              </button>

                              <button
                                onClick={() => handleDeleteLink(link)}
                                className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 cursor-pointer transition-colors"
                                title="Delete Link"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700/80 flex flex-col sm:flex-row items-center justify-between gap-3 bg-white dark:bg-gray-800">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Showing{" "}
                <span className="font-semibold text-gray-700 dark:text-gray-200">
                  {links.length === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1}
                </span>{" "}
                to{" "}
                <span className="font-semibold text-gray-700 dark:text-gray-200">
                  {Math.min(pagination.page * pagination.limit, pagination.total)}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-gray-700 dark:text-gray-200">
                  {pagination.total}
                </span>{" "}
                links
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
                  disabled={pagination.page <= 1}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                >
                  Previous
                </button>
                <span className="text-xs text-gray-600 dark:text-gray-400 font-semibold px-2">
                  Page {pagination.page} of {pagination.totalPages || 1}
                </span>
                <button
                  onClick={() =>
                    setPagination((p) => ({
                      ...p,
                      page: Math.min(p.totalPages || 1, p.page + 1),
                    }))
                  }
                  disabled={pagination.page >= (pagination.totalPages || 1)}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ─── MODAL: VIEW PHONE NUMBERS & CLICK TRACKING (SCREENSHOT 2 & 3) ─── */}
      <ViewPhoneNumbersModal
        isOpen={isViewPhoneModalOpen}
        onClose={() => {
          setIsViewPhoneModalOpen(false);
          setSelectedCampaignForView(null);
        }}
        campaignId={selectedCampaignForView?._id || selectedCampaignForView?.code}
        campaignData={selectedCampaignForView}
      />

      {/* ─── DRAWER: CREATE / EDIT SINGLE TINY URL ─── */}
      {isDrawerOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[99998] bg-black/50 backdrop-blur-xs transition-opacity duration-300 animate-in fade-in"
              onClick={handleCloseDrawer}
            />

            <div
              className="fixed top-0 right-0 z-[99999] h-full w-full max-w-lg bg-white dark:bg-gray-800 shadow-2xl flex flex-col border-l border-gray-200 dark:border-gray-700 animate-in slide-in-from-right duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800/80 flex-shrink-0">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-[#3a57e8] flex items-center justify-center">
                    <LinkIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 dark:text-white text-base">
                      {editingLink ? "Edit Tiny URL" : "Create New Tiny URL"}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Configure destination, custom code, and expiration
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseDrawer}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-200/50 dark:hover:bg-gray-700 cursor-pointer"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmitDrawer} className="flex-1 flex flex-col justify-between overflow-y-auto">
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block font-bold text-gray-700 dark:text-gray-200 mb-1.5 text-xs">
                      Destination URL <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="https://example.com/landing-page"
                      value={formData.originalDestinationUrl}
                      onChange={(e) => setFormData({ ...formData, originalDestinationUrl: e.target.value })}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3a57e8]"
                    />
                  </div>

                  <div>
                    <label className="block font-bold text-gray-700 dark:text-gray-200 mb-1.5 text-xs">
                      Title / Label <span className="text-gray-400 font-normal">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Summer Promo, Instagram Bio"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3a57e8]"
                    />
                  </div>

                  {!editingLink && (
                    <div>
                      <label className="block font-bold text-gray-700 dark:text-gray-200 mb-1.5 text-xs">
                        Custom Short Code <span className="text-gray-400 font-normal">(Optional 6 chars)</span>
                      </label>
                      <input
                        type="text"
                        maxLength={6}
                        placeholder="e.g. promo1, sale24 (auto-generated if empty)"
                        value={formData.customCode}
                        onChange={(e) => setFormData({ ...formData, customCode: e.target.value })}
                        className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono uppercase focus:outline-none focus:ring-2 focus:ring-[#3a57e8]"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block font-bold text-gray-700 dark:text-gray-200 mb-1.5 text-xs">
                      Expiration Rule
                    </label>
                    <select
                      value={formData.expirationPreset}
                      onChange={(e) => setFormData({ ...formData, expirationPreset: e.target.value })}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3a57e8]"
                    >
                      <option value="never">Never Expire</option>
                      <option value="24h">24 Hours</option>
                      <option value="7d">7 Days</option>
                      <option value="30d">30 Days</option>
                      <option value="custom">Custom Date & Time</option>
                    </select>
                  </div>

                  {formData.expirationPreset === "custom" && (
                    <div>
                      <label className="block font-bold text-gray-700 dark:text-gray-200 mb-1.5 text-xs">
                        Pick Expiration Date & Time
                      </label>
                      <input
                        type="datetime-local"
                        required
                        value={formData.customDate}
                        onChange={(e) => setFormData({ ...formData, customDate: e.target.value })}
                        className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3a57e8]"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block font-bold text-gray-700 dark:text-gray-200 mb-1.5 text-xs">
                      Description / Notes <span className="text-gray-400 font-normal">(Optional)</span>
                    </label>
                    <textarea
                      rows={3}
                      placeholder="Campaign notes, WhatsApp broadcast target, or owner remarks..."
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3a57e8]"
                    />
                  </div>

                  <div className="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-750 border border-gray-200 dark:border-gray-700 space-y-1">
                    <span className="text-[10px] uppercase font-bold tracking-wider text-gray-400">
                      Live Short Link Preview
                    </span>
                    <div className="font-mono text-xs font-semibold text-[#3a57e8] truncate">
                      {frontendBase}/{formData.customCode || (editingLink ? editingLink.code : "xYz123")}
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 dark:bg-gray-800/80 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3 flex-shrink-0">
                  <button
                    type="button"
                    onClick={handleCloseDrawer}
                    disabled={submitting}
                    className="px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 font-semibold cursor-pointer text-xs transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-[#3a57e8] hover:bg-[#2e47d4] text-white font-semibold shadow-md shadow-[#3a57e8]/30 cursor-pointer disabled:opacity-50 text-xs transition-all"
                  >
                    {submitting ? (
                      <>
                        <ArrowPathIcon className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : editingLink ? (
                      "Save Changes"
                    ) : (
                      "Create Tiny URL"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </>,
          document.body
        )}

      {/* ─── BULK PHONE CAMPAIGN CREATION DRAWER MODAL ─── */}
      <BulkCreateTinyUrlDrawer
        isOpen={isBulkDrawerOpen}
        onClose={() => {
          setIsBulkDrawerOpen(false);
          fetchCampaigns();
        }}
        onSuccess={() => {
          fetchCampaigns();
          fetchLinks();
        }}
      />
    </div>
  );
}
